import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ApiError } from '@/lib/api';
import { openBillingPortal, startCheckout, type PaidTier } from '@/lib/billing';
import { cn } from '@/lib/utils';
import { PRICING_DISPLAY, SALES_CONTACT_MAILTO, type PricingTierId } from '@/lib/waitlist';

// ---------------------------------------------------------------------------
// SettingsPlanGrid
// ---------------------------------------------------------------------------
//
// Compact 2×2 plan grid for the in-app Settings → Plan tab. Distinct from
// the marketing `<PricingCards>` component used on the landing page:
//
// - tighter padding, single-line description, smaller price font
// - inline price next to plan name (no big hero $-figure)
// - row-style features list, no reserved description height
// - "Manage billing" lives once at the top of the section (not on every
//   card), so the current-plan card just shows a non-actionable badge
//
// Goal: feel like a Settings panel, not a sales page.
// ---------------------------------------------------------------------------

export interface SettingsPlanGridProps {
  /** Tier the user is currently on (from `/api/me`). */
  currentTier: PricingTierId;
  /** Whether the user already has a Stripe customer (for "Switch to Free" → portal). */
  hasBillingCustomer: boolean;
}

export function SettingsPlanGrid({ currentTier, hasBillingCustomer }: SettingsPlanGridProps) {
  const [pending, setPending] = useState<PaidTier | 'portal' | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleUpgrade(tier: PaidTier) {
    setError(null);
    setPending(tier);
    try {
      await startCheckout(tier);
    } catch (err) {
      setPending(null);
      setError(err instanceof ApiError ? err.message : 'Could not start checkout.');
    }
  }

  async function handleManageBilling() {
    setError(null);
    setPending('portal');
    try {
      await openBillingPortal();
    } catch (err) {
      setPending(null);
      setError(err instanceof ApiError ? err.message : 'Could not open billing portal.');
    }
  }

  const onPaid = currentTier === 'pro' || currentTier === 'scale';

  return (
    <>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <PlanTile
          tier="free"
          current={currentTier === 'free'}
          ctaLabel={onPaid && hasBillingCustomer ? 'Switch to Free' : 'On Free plan'}
          ctaDisabled={!onPaid || !hasBillingCustomer}
          ctaLoading={onPaid && pending === 'portal'}
          onCta={onPaid && hasBillingCustomer ? handleManageBilling : undefined}
        />
        <PlanTile
          tier="pro"
          current={currentTier === 'pro'}
          ctaLabel={currentTier === 'pro' ? 'Current plan' : 'Upgrade to Pro'}
          ctaDisabled={currentTier === 'pro'}
          ctaLoading={pending === 'pro'}
          ctaVariant={currentTier === 'pro' ? 'outline' : 'default'}
          onCta={currentTier === 'pro' ? undefined : () => handleUpgrade('pro')}
        />
        <PlanTile
          tier="scale"
          current={currentTier === 'scale'}
          ctaLabel={currentTier === 'scale' ? 'Current plan' : 'Upgrade to Scale'}
          ctaDisabled={currentTier === 'scale'}
          ctaLoading={pending === 'scale'}
          ctaVariant={currentTier === 'scale' ? 'outline' : 'default'}
          onCta={currentTier === 'scale' ? undefined : () => handleUpgrade('scale')}
        />
        <PlanTile
          tier="custom"
          ctaLabel="Talk to us"
          ctaHref={SALES_CONTACT_MAILTO}
          ctaVariant="outline"
        />
      </div>

      {error ? (
        <Alert variant="destructive" className="mt-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}
    </>
  );
}

// ---------------------------------------------------------------------------
// PlanTile — compact single-tier card
// ---------------------------------------------------------------------------

interface PlanTileProps {
  tier: PricingTierId;
  current?: boolean;
  ctaLabel: string;
  ctaDisabled?: boolean;
  ctaLoading?: boolean;
  ctaHref?: string;
  ctaVariant?: 'default' | 'outline' | 'secondary';
  onCta?: () => void;
}

function PlanTile({
  tier,
  current = false,
  ctaLabel,
  ctaDisabled = false,
  ctaLoading = false,
  ctaHref,
  ctaVariant = 'default',
  onCta,
}: PlanTileProps) {
  const plan = PRICING_DISPLAY[tier];
  const isFree = tier === 'free';
  const isCustom = tier === 'custom';
  const priceDisplay = isFree ? 'Free' : isCustom ? 'Custom' : `$${plan.price}`;
  const showInterval = !isFree && !isCustom && plan.price !== null;

  return (
    <Card
      className={cn(
        'relative flex flex-col p-5 transition',
        current ? 'border-primary' : 'border-border',
      )}
    >
      <div className="flex items-baseline justify-between gap-3">
        <div className="flex items-baseline gap-2">
          <h3 className="text-base font-semibold tracking-tight">{plan.name}</h3>
          {current ? (
            <span className="text-primary text-[10px] font-semibold uppercase tracking-[0.14em]">
              Current
            </span>
          ) : null}
        </div>
        <div className="flex items-baseline gap-1">
          <span className="text-2xl font-semibold tracking-tight">{priceDisplay}</span>
          {showInterval ? (
            <span className="text-muted-foreground text-xs">/ mo</span>
          ) : null}
        </div>
      </div>

      <p className="text-muted-foreground mt-1.5 text-xs leading-relaxed">{plan.description}</p>

      <ul className="mt-4 grid gap-1.5 text-xs">
        {plan.features.map((f) => (
          <li key={f} className="flex items-start gap-2">
            <span className="bg-success mt-[5px] inline-block size-1 shrink-0 rounded-full" />
            <span className="text-foreground/85">{f}</span>
          </li>
        ))}
      </ul>

      <div className="mt-5">
        {ctaHref ? (
          <Button asChild variant={ctaVariant} size="sm" className="w-full">
            <a href={ctaHref}>{ctaLabel}</a>
          </Button>
        ) : (
          <Button
            type="button"
            onClick={onCta}
            disabled={ctaDisabled || ctaLoading}
            variant={ctaVariant}
            size="sm"
            className="w-full"
          >
            {ctaLoading ? <Loader2 className="size-3.5 animate-spin" /> : null}
            {ctaLabel}
          </Button>
        )}
      </div>
    </Card>
  );
}
